// src/pages/customer-panel/live-conversations/SudoProfilePage.js
// Agent Persona / Sudo Profile Management

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    Chip,
    Avatar,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveIcon from '@mui/icons-material/Save';
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';

const headCellSx = { fontFamily, fontWeight: 700, fontSize: 13, color: '#475569' };
const cellSx = { fontFamily, fontSize: 13 };

export default function SudoProfilePage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || '';
    const isAdmin = userRole === 'admin';

    // Own profile state
    const [profile, setProfile] = useState({
        display_name: '',
        role_title: '',
        agent_code: '',
    });
    const [profileLoading, setProfileLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profileError, setProfileError] = useState(null);

    // Admin: team profiles state
    const [teamProfiles, setTeamProfiles] = useState([]);
    const [teamProfilesLoading, setTeamProfilesLoading] = useState(false);
    const [teamProfilesError, setTeamProfilesError] = useState(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch own profile
    const fetchProfile = useCallback(async () => {
        try {
            setProfileLoading(true);
            const response = await axiosInstance.get('/live-chat/agent/sudo-profile');
            const data = response.data?.data || response.data?.profile || response.data || {};
            setProfile({
                display_name: data.sudo_name || data.display_name || data.displayName || '',
                role_title: data.role_designation || data.role_title || data.roleTitle || data.title || '',
                agent_code: data.agent_code || data.agentCode || '',
            });
            setProfileError(null);
        } catch (err) {
            console.error('Error fetching sudo profile:', err);
            setProfileError(err.response?.data?.message || 'Failed to load profile');
        } finally {
            setProfileLoading(false);
        }
    }, []);

    // Fetch team profiles (admin only)
    const fetchTeamProfiles = useCallback(async () => {
        if (!isAdmin) return;
        try {
            setTeamProfilesLoading(true);
            const response = await axiosInstance.get('/live-chat/agent/sudo-profiles');
            const resData = response.data?.data || {};
            const data = resData.items || resData.data || response.data?.profiles || [];
            setTeamProfiles(Array.isArray(data) ? data.map(p => ({
                ...p,
                name: p.real_name || p.name || p.full_name,
                display_name: p.sudo_name || p.display_name || p.displayName,
                role_title: p.role_designation || p.role_title || p.roleTitle || p.title,
            })) : []);
            setTeamProfilesError(null);
        } catch (err) {
            console.error('Error fetching team profiles:', err);
            setTeamProfilesError(err.response?.data?.message || 'Failed to load team profiles');
        } finally {
            setTeamProfilesLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchProfile();
        fetchTeamProfiles();
    }, [fetchProfile, fetchTeamProfiles]);

    // Save profile
    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!profile.display_name.trim()) {
            setSnackbar({ open: true, message: 'Display Name is required', severity: 'warning' });
            return;
        }
        try {
            setSaving(true);
            await axiosInstance.post('/live-chat/agent/sudo-profile', {
                sudo_name: profile.display_name.trim(),
                role_designation: profile.role_title.trim(),
                agent_code: profile.agent_code.trim(),
            });
            setSnackbar({ open: true, message: 'Profile saved successfully', severity: 'success' });
            // Refresh team profiles if admin
            if (isAdmin) fetchTeamProfiles();
        } catch (err) {
            console.error('Error saving profile:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to save profile',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle field change
    const handleChange = (field) => (e) => {
        setProfile((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // Get initials
    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((w) => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <BadgeIcon sx={{ color: ACCENT, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                    Sudo Profile
                </Typography>
            </Box>

            {/* Personal Profile Form */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <PersonOutlineIcon sx={{ color: ACCENT, fontSize: 22 }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                        Your Agent Profile
                    </Typography>
                </Box>
                <Divider />

                {profileLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: ACCENT }} />
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSaveProfile} sx={{ p: 3 }}>
                        {profileError && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2, fontFamily }}
                                onClose={() => setProfileError(null)}
                            >
                                {profileError}
                            </Alert>
                        )}

                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Display Name"
                                    value={profile.display_name}
                                    onChange={handleChange('display_name')}
                                    required
                                    placeholder="e.g. John Smith"
                                    InputLabelProps={{ sx: { fontFamily, fontSize: 14 } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px', fontSize: 14 } }}
                                    helperText="Name visible to customers in live chat"
                                    FormHelperTextProps={{ sx: { fontFamily, fontSize: 11 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Role / Title"
                                    value={profile.role_title}
                                    onChange={handleChange('role_title')}
                                    placeholder="e.g. Support Agent, Team Lead"
                                    InputLabelProps={{ sx: { fontFamily, fontSize: 14 } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px', fontSize: 14 } }}
                                    helperText="Your role or title displayed in the chat"
                                    FormHelperTextProps={{ sx: { fontFamily, fontSize: 11 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Agent Code"
                                    value={profile.agent_code}
                                    onChange={handleChange('agent_code')}
                                    placeholder="e.g. AGT-001"
                                    InputLabelProps={{ sx: { fontFamily, fontSize: 14 } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px', fontSize: 14 } }}
                                    helperText="Unique agent identifier code"
                                    FormHelperTextProps={{ sx: { fontFamily, fontSize: 11 } }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={saving}
                                startIcon={
                                    saving ? (
                                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    bgcolor: ACCENT,
                                    px: 3,
                                    py: 1,
                                    '&:hover': { bgcolor: ACCENT_DARK },
                                }}
                            >
                                {saving ? 'Saving...' : 'Save Profile'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Admin: Team Profiles Table */}
            {isAdmin && (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: 2.5,
                            py: 2,
                            bgcolor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <GroupIcon sx={{ color: ACCENT, fontSize: 22 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                                Team Sudo Profiles
                            </Typography>
                            <Chip
                                label={`${teamProfiles.length} agents`}
                                size="small"
                                sx={{
                                    fontFamily,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    height: 22,
                                    bgcolor: `${ACCENT}15`,
                                    color: ACCENT,
                                }}
                            />
                        </Box>
                    </Box>
                    <Divider />

                    {teamProfilesError && (
                        <Alert
                            severity="error"
                            sx={{ m: 2, fontFamily }}
                            onClose={() => setTeamProfilesError(null)}
                        >
                            {teamProfilesError}
                        </Alert>
                    )}

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fafbfc' }}>
                                    <TableCell sx={headCellSx}>Agent</TableCell>
                                    <TableCell sx={headCellSx}>Display Name</TableCell>
                                    <TableCell sx={headCellSx}>Role / Title</TableCell>
                                    <TableCell sx={headCellSx}>Agent Code</TableCell>
                                    <TableCell sx={headCellSx} align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {teamProfilesLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={32} sx={{ color: ACCENT }} />
                                        </TableCell>
                                    </TableRow>
                                ) : teamProfiles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                            <GroupIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: 14 }}>
                                                No team profiles found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    teamProfiles.map((member, idx) => (
                                        <TableRow
                                            key={member.user_id || member.id || idx}
                                            hover
                                            sx={{ '&:hover': { bgcolor: `${ACCENT}06` } }}
                                        >
                                            <TableCell sx={cellSx}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 34,
                                                            height: 34,
                                                            bgcolor: ACCENT,
                                                            fontSize: 13,
                                                            fontFamily,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {getInitials(member.display_name || member.name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily,
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                color: '#1e293b',
                                                            }}
                                                        >
                                                            {member.name || member.full_name || '--'}
                                                        </Typography>
                                                        {member.email && (
                                                            <Typography sx={{ fontFamily, fontSize: 11, color: '#94a3b8' }}>
                                                                {member.email}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                                    {member.display_name || member.displayName || '--'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {member.role_title || member.roleTitle || member.title ? (
                                                    <Chip
                                                        label={member.role_title || member.roleTitle || member.title}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontFamily,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            height: 22,
                                                            borderColor: '#e2e8f0',
                                                            color: '#475569',
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontFamily, fontSize: 12, color: '#94a3b8' }}>
                                                        --
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography
                                                    sx={{
                                                        fontFamily,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        color: ACCENT,
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {member.agent_code || member.agentCode || '--'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={member.is_active || member.isActive ? 'Active' : 'Inactive'}
                                                    color={member.is_active || member.isActive ? 'success' : 'default'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        height: 22,
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ fontFamily }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
